import { isWithinInterval } from "date-fns";
import { DataSeriesUnit, TimeFrame } from "src/components/monitoring/commonTypes";
import { LineChartItem } from "src/components/monitoring/senec-line-chart/lineChartTypes";
import dayjs from "dayjs";

export const NO_VALUE_INDICATOR = "-";

/**
 * Formats a numeric value according to locale for display in the legend, adjusting the decimal place to the size of the number.
 * @returns {string} Formatted string representation of the provided value.
 *                   If the value is 100 or greater, no decimal places are shown.
 *                   If the value is less than 1, two decimal places are shown.
 *                   Otherwise, one decimal place is shown.
 *                   If the value cannot be converted, "-" is returned.
 */
export function formatLegendValue(
    locale: string,
    value?: number,
    showNoValueIndicator = true,
    alwaysOneDecimal = false, // Except for percentages
    unit?: DataSeriesUnit,
): string {
    if (value === undefined) return showNoValueIndicator ? NO_VALUE_INDICATOR : "";

    const precision = calculatePrecision(value, alwaysOneDecimal, unit);
    const multiplier = Math.pow(10, precision);
    const roundedValue = Math.round(value * multiplier) / multiplier;

    return roundedValue.toLocaleString(locale, {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
    });
}

function calculatePrecision(value: number, alwaysOneDecimal: boolean, unit?: DataSeriesUnit): number {
    if (unit === DataSeriesUnit.PERCENT) return 0;
    if (alwaysOneDecimal) return 1;
    return value >= 100 ? 0 : value < 1 ? 2 : 1;
}

/**
 *  Finds highest value in data array for a given time frame and specified DataSeriesUnit. Values below 500 watt are returned as WATT otherwise as KILOWATT. TIME values are returned with 24 as highest value. PERCENT values are returned with 100 as highest value. KWH and other units are not handled.
 */
export function getHighestValueBetweenTimeFrameForUnit(
    data: LineChartItem[],
    timeFrame: TimeFrame,
    unit: DataSeriesUnit | undefined,
): { highestLabelValue: number | undefined; highestDataValue: number; unit: DataSeriesUnit } {
    let highestValue = 0;
    let unitValue: DataSeriesUnit = unit ?? DataSeriesUnit.KILOWATT;

    const filteredData =
        unit != undefined
            ? data.filter((line) => line.unit === unit)
            : data.filter((line) => line.unit != DataSeriesUnit.PERCENT);
    if (filteredData.length === 0) {
        return { highestLabelValue: undefined, highestDataValue: 0, unit: unitValue };
    }

    if (unit === DataSeriesUnit.TIME) {
        return { highestLabelValue: 24, highestDataValue: 24, unit: DataSeriesUnit.TIME };
    } else if (unit === DataSeriesUnit.PERCENT) {
        return { highestLabelValue: 100, highestDataValue: 100, unit: DataSeriesUnit.PERCENT };
    }

    for (const line of filteredData) {
        const unit = line.unit;

        const filteredPoints = line.data.filter((point) =>
            isWithinInterval(point.x, { start: dayjs(timeFrame.start).toDate(), end: dayjs(timeFrame.end).toDate() }),
        );

        const lineHighestValue = Math.max(...filteredPoints.map((point) => point.y), Number.MIN_SAFE_INTEGER);

        if (lineHighestValue > highestValue) {
            highestValue = lineHighestValue;
            unitValue = unit;
        }
    }

    if (unitValue === DataSeriesUnit.WATT && highestValue <= 500) {
        return { highestLabelValue: highestValue, highestDataValue: highestValue, unit: DataSeriesUnit.WATT };
    } else if (unitValue === DataSeriesUnit.WATT && highestValue > 500) {
        return { highestLabelValue: highestValue / 1000, highestDataValue: highestValue, unit: DataSeriesUnit.KILOWATT };
    }

    return { highestLabelValue: highestValue, highestDataValue: highestValue, unit: unitValue };
}
